import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Download, 
  Send, 
  MoreVertical,
  Calendar,
  User,
  DollarSign,
  Tag,
  Settings,
  Save,
  X,
  Check,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import QuotationInvoiceDefaults from './QuotationInvoiceDefaults';

const QuotationsManagement = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDefaultsModal, setShowDefaultsModal] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedQuotations, setSelectedQuotations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Form state
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    billing_address: '',
    shipping_address: '',
    sales_agent: '',
    sales_agent_id: '',
    reference: '',
    currency: 'USD',
    discount_type: 'none',
    discount_value: 0,
    discount_percentage: 0,
    frontend_discount_type: 'fixed',
    adjustment: 0,
    client_note: '',
    terms_conditions: '',
    admin_note: '',
    quotation_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    tags: '',
    status: 'draft',
    items: []
  });
  
  // Defaults state
  const [defaults, setDefaults] = useState({
    logo: '',
    company_name: 'Niswa Info Tech',
    company_website: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pin_code: '',
    phone_number: '',
    default_taxes: [],
    show_tax: true,
    show_currency_symbol: true,
    default_currency: 'USD',
    show_total_in_words: true,
    total_in_words_format: 'normal',
    quotation_prefix: 'QUO',
    next_quotation_number: 1,
    quotation_due_after_days: 30,
    show_project_name_quotation: true,
    show_total_paid_quotation: true,
    show_amount_due_quotation: true,
    default_quotation_client_note: '',
    default_quotation_terms: '',
    quotation_info_format: {},
    default_payment_details: {}
  });

  useEffect(() => {
    fetchQuotations();
    fetchDefaults();
    fetchUsers();
    fetchProducts();
  }, [currentPage, searchTerm, statusFilter]);

  // When defaults load and opening a new quotation, apply defaults
  useEffect(() => {
    if (showModal && !editingQuotation && defaults) {
      resetForm();
    }
  }, [showModal, editingQuotation, defaults]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container') && !event.target.closest('.dropdown-portal')) {
        setShowCustomerDropdown(false);
        setShowProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter })
      });
      
      const response = await api.get(`/quotations?${params}`);
      if (response.data.success) {
        setQuotations(response.data.data);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (error) {
      console.error('Error fetching quotations:', error);
      toast.error('Failed to fetch quotations');
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaults = async () => {
    try {
      const response = await api.get('/quotation-invoice-defaults');
      if (response.data.success) {
        setDefaults(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching defaults:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      if (response.data.success) {
        setUsers(response.data.data.users || []);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchProducts = async () => {
    try {
      // Fetch all products by setting a high limit and status=all for admin
      const response = await api.get('/products?limit=1000&status=all');
      console.log('Products API response:', response.data);
      if (response.data.success) {
        const productsData = (response.data.data.products || []).map((p) => ({
          ...p,
          title: p.title || p.name || '',
          short_description: p.short_description || p.description || '',
        }));
        console.log('Products loaded:', productsData.length, productsData);
        setProducts(productsData);
      } else {
        console.log('Products API failed:', response.data);
        setProducts([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  const handleSaveAndSend = async () => {
    setLoading(true);
    try {
      const response = editingQuotation 
        ? await api.put(`/quotations/${editingQuotation.id}`, formData)
        : await api.post('/quotations', formData);
      
      if (response.data.success) {
        toast.success(editingQuotation ? 'Quotation updated successfully!' : 'Quotation created successfully!');
        fetchQuotations();
        setShowModal(false);
        setEditingQuotation(null);
        resetForm();
        
        // Open sending options modal
        // TODO: Implement sending options modal
        toast.info('Sending options will be implemented soon');
      }
    } catch (error) {
      console.error('Error saving quotation:', error);
      toast.error(error.response?.data?.message || 'Failed to save quotation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingQuotation) {
        await api.put(`/quotations/${editingQuotation.id}`, formData);
        toast.success('Quotation updated successfully');
      } else {
        await api.post('/quotations', formData);
        toast.success('Quotation created successfully');
      }
      
      setShowModal(false);
      setEditingQuotation(null);
      resetForm();
      fetchQuotations();
    } catch (error) {
      console.error('Error saving quotation:', error);
      toast.error('Failed to save quotation');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (quotation) => {
    try {
      setLoading(true);
      // Fetch full quotation with items to ensure products appear in editor
      const resp = await api.get(`/quotations/${quotation.id}`);
      const full = resp?.data?.data || quotation;

      // Normalize items into UI shape
      const items = (full.items || []).map((it, idx) => {
        const quantity = parseInt(it.quantity) || 1;
        const unit_price = parseFloat(it.unit_price ?? it.license_price) || 0;
        const original_price = parseFloat(it.original_price ?? it.price_original) || 0;
        const discount_percentage = parseFloat(it.discount_percentage) || 0;
        const tax_rate = parseFloat(it.tax_rate) || 0;
        const extended_support_price = parseFloat(it.extended_support_price) || 0;
        const addons_price = parseFloat(it.addons_price) || 0;
        const total = parseFloat(it.total ?? it.total_amount) || (unit_price * quantity);
        return {
          id: it.id || idx + 1,
          item_type: it.item_type || 'product',
          product_id: it.product_id ?? null,
          title: it.title || it.product_title || it.name || '',
          short_description: it.short_description || it.product_description || '',
          quantity,
          unit_price,
          original_price,
          discount_percentage,
          tax_rate,
          extended_support_price,
          addons_price,
          total,
          item_order: it.item_order ?? idx
        };
      });

      // Reconstruct frontend-specific fields from backend data
      const reconstructedData = {
        ...full,
        items,
        // Reconstruct adjustment_sign from stored adjustment value
        adjustment_sign: (full.adjustment || 0) < 0 ? '-' : '+',
        adjustment: Math.abs(full.adjustment || 0),
        // Reconstruct frontend_discount_type from stored discount values
        frontend_discount_type: (full.discount_percentage || 0) > 0 ? 'percentage' : 'fixed',
        // Keep the original discount_type (before_tax/after_tax) from database
        discount_type: full.discount_type || 'none',
        // Reconstruct discount values properly
        discount_value: full.discount_percentage > 0 ? full.discount_percentage : full.discount_value,
        // Format dates for HTML date inputs (YYYY-MM-DD format)
        quotation_date: full.quotation_date ? new Date(full.quotation_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        expiry_date: full.expiry_date ? new Date(full.expiry_date).toISOString().split('T')[0] : ''
      };

      setEditingQuotation(full);
      setFormData(reconstructedData);
      setShowModal(true);
    } catch (error) {
      console.error('Failed to load quotation for edit:', error);
      toast.error('Failed to load quotation details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quotation?')) return;
    
    try {
      await api.delete(`/quotations/${id}`);
      toast.success('Quotation deleted successfully');
      fetchQuotations();
    } catch (error) {
      console.error('Error deleting quotation:', error);
      toast.error('Failed to delete quotation');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedQuotations.length === 0) {
      toast.warning('Please select quotations to perform bulk action');
      return;
    }
    
    try {
      await api.post('/quotations/bulk', {
        action,
        ids: selectedQuotations
      });
      toast.success(`Bulk ${action} completed successfully`);
      setSelectedQuotations([]);
      fetchQuotations();
    } catch (error) {
      console.error('Error performing bulk action:', error);
      toast.error('Failed to perform bulk action');
    }
  };

  // Helper to add days to a YYYY-MM-DD string (or today)
  const addDays = (startDateStr, days) => {
    const d = startDateStr ? new Date(startDateStr) : new Date();
    d.setDate(d.getDate() + (parseInt(days) || 0));
    return d.toISOString().split('T')[0];
  };

  const resetForm = () => {
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      customer_id: '',
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      billing_address: '',
      shipping_address: '',
      sales_agent: '',
      sales_agent_id: '',
      reference: '',
      currency: defaults.default_currency || 'USD',
      discount_type: formData.discount_type || 'fixed',
      discount_value: 0,
      discount_percentage: 0,
      frontend_discount_type: 'fixed',
      adjustment_sign: '+',
      adjustment: 0,
      client_note: defaults.default_quotation_client_note || '',
      terms_conditions: defaults.default_quotation_terms || '',
      admin_note: '',
      quotation_date: today,
      expiry_date: addDays(today, defaults.quotation_due_after_days || 0),
      tags: '',
      status: 'draft',
      items: []
    });
  };

  const handleCustomerSelect = (user) => {
    setFormData(prev => ({
      ...prev,
      customer_id: user.id,
      customer_name: user.name,
      customer_email: user.email,
      customer_phone: user.phone || ''
    }));
    setShowCustomerDropdown(false);
  };

  const [showSalesAgentDropdown, setShowSalesAgentDropdown] = useState(false);

  const getSalesAgentSuggestions = () => {
    const term = (formData.sales_agent || '').toLowerCase();
    if (!term) return (users || []); // show all when focusing with empty term
    return (users || []).filter(user =>
      user && user.name && (
        user.name.toLowerCase().includes(term) || (user.email || '').toLowerCase().includes(term)
      )
    );
  };

  const handleSalesAgentSelect = (user) => {
    setFormData(prev => ({
      ...prev,
      sales_agent: user.name || '',
      sales_agent_id: user.id || ''
    }));
    setShowCustomerDropdown(false);
    setShowProductDropdown(false);
    setShowSalesAgentDropdown(false);
  };

  const handleProductSelect = (product, licenseType = 'regular') => {
    const isExtended = licenseType === 'extended';

    const unitPrice = parseFloat(isExtended ? product.extended_license_price : product.regular_license_price) || 0;
    const originalPrice = parseFloat(isExtended ? product.extended_license_original_price : product.regular_license_original_price) || 0;

    const discountPct = originalPrice > 0 && unitPrice < originalPrice
      ? parseFloat((((originalPrice - unitPrice) / originalPrice) * 100).toFixed(2))
      : 0;

    const newItem = {
      id: Date.now(),
      item_type: 'product',
      product_id: product.id,
      title: `${product.title || product.name}${isExtended ? ' - Extended License' : ' - Regular License'}`,
      short_description: product.short_description || product.description || '',
      quantity: 1,
      unit_price: unitPrice,
      original_price: originalPrice,
      discount_percentage: discountPct,
      tax_rate: 0,
      extended_support_price: 0,
      addons_price: 0,
      total: unitPrice,
      item_order: formData.items.length
    };

    const itemsToAdd = [newItem];

    // Add Extended Support as a separate item if available
    const supportPrice = parseFloat(product.extended_support_price) || 0;
    const supportOriginal = parseFloat(product.extended_support_original_price) || 0;
    const supportDiscountPct = supportOriginal > 0 && supportPrice < supportOriginal
      ? parseFloat((((supportOriginal - supportPrice) / supportOriginal) * 100).toFixed(2))
      : 0;
    if (supportPrice > 0) {
      itemsToAdd.push({
        id: Date.now() + 1,
        item_type: 'custom',
        product_id: product.id,
        title: 'Extended Support',
        short_description: product.extended_support_text || 'Extended Support',
        quantity: 1,
        unit_price: supportPrice,
        original_price: supportOriginal,
        discount_percentage: supportDiscountPct,
        tax_rate: 0,
        extended_support_price: 0,
        addons_price: 0,
        total: supportPrice,
        item_order: formData.items.length + itemsToAdd.length
      });
    }

    // Add Addons as separate items if present
    let addons = [];
    try {
      addons = Array.isArray(product.addons) ? product.addons : JSON.parse(product.addons || '[]');
    } catch (_) {
      addons = [];
    }
    if (Array.isArray(addons)) {
      addons.forEach((addon, idx) => {
        const addonTitle = addon?.title || addon?.name;
        const addonPrice = parseFloat(addon?.price) || 0;
        const addonOriginal = parseFloat(addon?.original_price || addon?.price_original) || 0;
        const addonDiscountPct = addonOriginal > 0 && addonPrice < addonOriginal
          ? parseFloat((((addonOriginal - addonPrice) / addonOriginal) * 100).toFixed(2))
          : 0;
        if (addonTitle && addonPrice > 0) {
          itemsToAdd.push({
            id: Date.now() + 2 + idx,
            item_type: 'custom',
            product_id: product.id,
            title: addonTitle,
            short_description: addon?.description || 'Addon',
            quantity: 1,
            unit_price: addonPrice,
            original_price: addonOriginal,
            discount_percentage: addonDiscountPct,
            tax_rate: 0,
            extended_support_price: 0,
            addons_price: 0,
            total: addonPrice,
            item_order: formData.items.length + itemsToAdd.length
          });
        }
      });
    }

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, ...itemsToAdd]
    }));

    setProductSearchTerm('');
    setShowProductDropdown(false);
  };

  const addCustomProduct = () => {
    const newItem = {
      id: Date.now(),
      item_type: 'custom',
      product_id: null,
      title: '',
      short_description: '',
      quantity: 1,
      unit_price: 0,
      original_price: 0,
      discount_percentage: 0,
      tax_rate: 0,
      extended_support_price: 0,
      addons_price: 0,
      total: 0,
      item_order: formData.items.length
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateItem = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          // Recalculate total
          const total = (updatedItem.quantity * updatedItem.unit_price) + 
                      (updatedItem.extended_support_price || 0) + 
                      (updatedItem.addons_price || 0);
          updatedItem.total = total;
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const removeItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const moveItemUp = (itemId) => {
    setFormData(prev => {
      const items = [...prev.items];
      const index = items.findIndex(item => item.id === itemId);
      if (index > 0) {
        [items[index], items[index - 1]] = [items[index - 1], items[index]];
        // Update item_order
        items.forEach((item, idx) => {
          item.item_order = idx;
        });
      }
      return { ...prev, items };
    });
  };

  const moveItemDown = (itemId) => {
    setFormData(prev => {
      const items = [...prev.items];
      const index = items.findIndex(item => item.id === itemId);
      if (index < items.length - 1) {
        [items[index], items[index + 1]] = [items[index + 1], items[index]];
        // Update item_order
        items.forEach((item, idx) => {
          item.item_order = idx;
        });
      }
      return { ...prev, items };
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'declined': return 'bg-red-100 text-red-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Currency formatting helpers
  const getCurrencySymbol = (code) => {
    const map = { USD: '$', INR: '₹', EUR: '€', GBP: '£', AED: 'د.إ', SAR: '﷼', AUD: 'A$', CAD: 'C$', JPY: '¥' };
    return map[code] || code || '$';
  };

  const formatCurrency = (value) => {
    const amount = parseFloat(value) || 0;
    const code = formData.currency || defaults.default_currency || 'USD';
    const symbolOrCode = defaults.show_currency_symbol ? getCurrencySymbol(code) : code;
    return `${symbolOrCode}${amount.toFixed(2)}`;
  };

  const viewQuotationPdf = async (id) => {
    try {
      const response = await api.get(`/pdf/quotation/${id}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error opening quotation PDF:', error);
      toast.error('Failed to open PDF');
    }
  };

  const downloadQuotationPdf = async (quotation) => {
    try {
      const url = `/pdf/quotation/${quotation.id}`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Quotation-${quotation.quotation_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Error downloading quotation PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const getCustomerSuggestions = () => {
    const term = (formData.customer_name || '').toLowerCase();
    if (!term) return [];
    return (users || []).filter(user =>
      user && user.name && user.email && (
        user.name.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term)
      )
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Quotations Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage and track your quotations</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowDefaultsModal(true)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center"
          >
            <Settings className="w-4 h-4 mr-2" />
            Defaults
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Quotation
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search quotations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="expired">Expired</option>
              <option value="declined">Declined</option>
              <option value="accepted">Accepted</option>
            </select>
            <button
              onClick={fetchQuotations}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedQuotations.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-blue-800 dark:text-blue-200">
              {selectedQuotations.length} quotation(s) selected
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => handleBulkAction('mark_sent')}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                Mark as Sent
              </button>
              <button
                onClick={() => handleBulkAction('activate')}
                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
              >
                Activate
              </button>
              <button
                onClick={() => handleBulkAction('deactivate')}
                className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
              >
                Deactivate
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quotations Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedQuotations(quotations.map(q => q.id));
                      } else {
                        setSelectedQuotations([]);
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Quotation #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                      Loading quotations...
                    </div>
                  </td>
                </tr>
              ) : quotations.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No quotations found
                  </td>
                </tr>
              ) : (
                quotations.map((quotation) => (
                  <tr key={quotation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedQuotations.includes(quotation.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedQuotations([...selectedQuotations, quotation.id]);
                          } else {
                            setSelectedQuotations(selectedQuotations.filter(id => id !== quotation.id));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {quotation.quotation_number}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      <div>
                        <div className="font-medium">{quotation.customer_name}</div>
                        <div className="text-gray-500 dark:text-gray-400">{quotation.customer_email}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {quotation.currency} {(parseFloat(quotation.total_amount) || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(quotation.status)}`}>
                        {quotation.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {new Date(quotation.quotation_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => viewQuotationPdf(quotation.id)}
                          className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="View PDF"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadQuotationPdf(quotation)}
                          className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(quotation)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(quotation.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-gray-700 dark:text-gray-300">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Defaults Modal */}
      {showDefaultsModal && createPortal(
        <QuotationInvoiceDefaults onClose={() => setShowDefaultsModal(false)} />,
        document.body
      )}

      {/* New/Edit Quotation Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="overflow-visible">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingQuotation ? 'Edit Quotation' : 'New Quotation'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingQuotation(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Customer Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative dropdown-container">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Customer Name *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        value={formData.customer_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                        onFocus={() => setShowCustomerDropdown(true)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        placeholder="Type to search or enter manually"
                      />
                      {showCustomerDropdown && (
                        <div className="absolute z-[60] w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {getCustomerSuggestions().map(user => (
                            <div
                              key={user.id}
                              onClick={() => handleCustomerSelect(user)}
                              className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-200 dark:border-gray-600"
                            >
                              <div className="font-medium text-gray-900 dark:text-white">{user.name || 'Unknown'}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{user.email || 'No email'}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {showCustomerDropdown && (() => {
                        const h = Math.min(240, getCustomerSuggestions().length * 44);
                        return <div style={{ height: h }} />;
                      })()}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Currency
                    </label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="INR">INR</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                    <select
                      value={formData.status || 'draft'}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="expired">Expired</option>
                      <option value="declined">Declined</option>
                      <option value="accepted">Accepted</option>
                    </select>
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Billing Address
                  </label>
                  <textarea
                    value={formData.billing_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, billing_address: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Quotation Details */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Quotation Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Quotation Date
                    </label>
                    <input
                      type="date"
                      value={formData.quotation_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, quotation_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Expiry Date
                    </label>
                    <input
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sales Agent</label>
                    <div className="relative dropdown-container">
                      <input
                        type="text"
                        value={formData.sales_agent}
                        onChange={(e) => setFormData(prev => ({ ...prev, sales_agent: e.target.value }))}
                        onFocus={() => setShowSalesAgentDropdown(true)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Select or type sales agent"
                      />
                      {showSalesAgentDropdown && (
                        <div className="absolute z-[60] w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {getSalesAgentSuggestions().map(user => (
                            <div
                              key={user.id}
                              onClick={() => handleSalesAgentSelect(user)}
                              className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-200 dark:border-gray-600"
                            >
                              <div className="font-medium text-gray-900 dark:text-white">{user.name}</div>
                              {user.email && (
                                <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {showSalesAgentDropdown && (() => {
                        const h = Math.min(240, getSalesAgentSuggestions().length * 44);
                        return <div style={{ height: h }} />;
                      })()}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Reference
                    </label>
                    <input
                      type="text"
                      value={formData.reference}
                      onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tags
                    </label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                      placeholder="Enter tags separated by commas"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Notes & Terms</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Client Note
                    </label>
                    <textarea
                      value={formData.client_note}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_note: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Admin Note
                    </label>
                    <textarea
                      value={formData.admin_note}
                      onChange={(e) => setFormData(prev => ({ ...prev, admin_note: e.target.value }))}
                      rows={3}
                      placeholder="Internal notes (not visible to customer)"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Terms & Conditions
                    </label>
                    <textarea
                      value={formData.terms_conditions}
                      onChange={(e) => setFormData(prev => ({ ...prev, terms_conditions: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Discount & Adjustment */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Discount & Adjustment</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Discount Type
                    </label>
                    <select
                      value={formData.discount_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, discount_type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="none">No Discount</option>
                      <option value="before_tax">Before Tax</option>
                      <option value="after_tax">After Tax</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Discount Value
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.discount_value}
                      onChange={(e) => setFormData(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Adjustment
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.adjustment}
                      onChange={(e) => setFormData(prev => ({ ...prev, adjustment: parseFloat(e.target.value) || 0 }))}
                      placeholder="Add or subtract amount"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Products Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Products & Items</h3>
                
                {/* Add Product Section */}
                <div className="mb-4 p-4 border border-gray-300 dark:border-gray-600 rounded-lg">
                  <div className="flex gap-4 mb-4">
                    <div className="flex-1 relative dropdown-container">
                      <input
                        type="text"
                        placeholder="Search products..."
                        value={productSearchTerm}
                        onChange={(e) => setProductSearchTerm(e.target.value)}
                        onFocus={() => setShowProductDropdown(true)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      />
                      {showProductDropdown && createPortal(
                        <div className="fixed z-[70] bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto dropdown-portal" style={{ 
                          position: 'fixed',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '400px',
                          maxHeight: '300px'
                        }}>
                          {console.log('Rendering dropdown with products:', products?.length, 'search term:', productSearchTerm)}
                          {(products || []).filter(product => 
                            (product && (product.title || product.name)) && 
                            (!productSearchTerm || (product.title || product.name).toLowerCase().includes(productSearchTerm.toLowerCase()))
                          ).map(product => (
                            <div
                              key={product.id}
                              className="px-4 py-2 border-b border-gray-200 dark:border-gray-600"
                            >
                              <div className="flex items-center justify-between">
                                <div
                                  onClick={() => handleProductSelect(product, 'regular')}
                                  className="cursor-pointer"
                                >
                                  <div className="font-medium text-gray-900 dark:text-white">{product.title || product.name || 'Unknown Product'}</div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {formatCurrency(product.regular_license_price)} - {formatCurrency(product.extended_license_price)}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleProductSelect(product, 'extended')}
                                  className="ml-3 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                  Add Extended
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>,
                        document.body
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={addCustomProduct}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Custom Item
                    </button>
                  </div>
                </div>

                {/* Products List */}
                <div className="space-y-4">
                  {formData.items.map((item) => (
                    <div key={item.id} className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                            #{formData.items.findIndex(i => i.id === item.id) + 1}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {item.item_type === 'product' ? 'Product' : 'Custom Item'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => moveItemUp(item.id)}
                            disabled={formData.items.findIndex(i => i.id === item.id) === 0}
                            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => moveItemDown(item.id)}
                            disabled={formData.items.findIndex(i => i.id === item.id) === formData.items.length - 1}
                            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            className="p-1 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Title *
                          </label>
                          <input
                            type="text"
                            value={item.title}
                            onChange={(e) => updateItem(item.id, 'title', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Quantity *
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Unit Price *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => updateItem(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Original Price
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.original_price}
                            onChange={(e) => updateItem(item.id, 'original_price', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Discount %
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.discount_percentage}
                            onChange={(e) => updateItem(item.id, 'discount_percentage', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Tax Rate %
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.tax_rate}
                            onChange={(e) => updateItem(item.id, 'tax_rate', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Short Description
                        </label>
                        <textarea
                          value={item.short_description}
                          onChange={(e) => updateItem(item.id, 'short_description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Summary Section */}
                <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex justify-end">
                    <div className="w-full md:w-1/2 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Subtotal</span>
                        <span className="text-base font-medium text-gray-900 dark:text-white">
                          {formatCurrency(formData.items.reduce((sum, it) => sum + (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 1), 0))}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Currency</span>
                        <span className="text-xs text-gray-700 dark:text-gray-300">{formData.currency || defaults.default_currency || 'USD'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-gray-600 dark:text-gray-300 flex-1">Discount</label>
                        <div className="flex items-center gap-2">
                          <select
                            value={formData.frontend_discount_type || 'fixed'}
                            onChange={(e) => setFormData(prev => ({ ...prev, frontend_discount_type: e.target.value }))}
                            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          >
                            <option value="fixed">Fixed</option>
                            <option value="percentage">Percentage</option>
                          </select>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.discount_value}
                            onChange={(e) => setFormData(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                            className="w-32 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-right"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-gray-600 dark:text-gray-300 flex-1">Adjustment</label>
                        <div className="flex items-center gap-2">
                          <select
                            value={formData.adjustment_sign || '+'}
                            onChange={(e) => setFormData(prev => ({ ...prev, adjustment_sign: e.target.value }))}
                            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          >
                            <option value="+">+</option>
                            <option value="-">-</option>
                          </select>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.adjustment}
                            onChange={(e) => setFormData(prev => ({ ...prev, adjustment: parseFloat(e.target.value) || 0 }))}
                            className="w-32 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-right"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Tax Amount</span>
                        <span className="text-base font-medium text-gray-900 dark:text-white">
                          {(() => {
                            const subtotal = formData.items.reduce((sum, it) => sum + (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 1), 0);
                            const discountVal = parseFloat(formData.discount_value) || 0;
                            const discount = (formData.frontend_discount_type === 'percentage')
                              ? (subtotal * discountVal) / 100
                              : discountVal;
                            
                            // Calculate tax based on discount type
                            let taxAmount = 0;
                            if (formData.discount_type === 'before_tax') {
                              // Apply discount first, then calculate tax on discounted amount
                              const discountRatio = discount / subtotal;
                              taxAmount = formData.items.reduce((sum, it) => {
                                const itemTotal = (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 1);
                                const itemTaxRate = parseFloat(it.tax_rate) || 0;
                                // Calculate tax on discounted item total
                                const discountedItemTotal = itemTotal * (1 - discountRatio);
                                return sum + (discountedItemTotal * itemTaxRate / 100);
                              }, 0);
                            } else {
                              // Calculate tax on original subtotal first (before discount)
                              taxAmount = formData.items.reduce((sum, it) => {
                                const itemTotal = (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 1);
                                const itemTaxRate = parseFloat(it.tax_rate) || 0;
                                return sum + (itemTotal * itemTaxRate / 100);
                              }, 0);
                            }
                            
                            return formatCurrency(taxAmount);
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">Total</span>
                        <span className="text-lg font-bold text-gray-900 dark:text-white">
                          {(() => {
                            const subtotal = formData.items.reduce((sum, it) => sum + (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 1), 0);
                            const discountVal = parseFloat(formData.discount_value) || 0;
                            const discount = (formData.frontend_discount_type === 'percentage')
                              ? (subtotal * discountVal) / 100
                              : discountVal;
                            
                            // Calculate tax based on discount type
                            let taxAmount = 0;
                            if (formData.discount_type === 'before_tax') {
                              // Apply discount first, then calculate tax on discounted amount
                              const discountRatio = discount / subtotal;
                              taxAmount = formData.items.reduce((sum, it) => {
                                const itemTotal = (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 1);
                                const itemTaxRate = parseFloat(it.tax_rate) || 0;
                                // Calculate tax on discounted item total
                                const discountedItemTotal = itemTotal * (1 - discountRatio);
                                return sum + (discountedItemTotal * itemTaxRate / 100);
                              }, 0);
                            } else {
                              // Calculate tax on original subtotal first (before discount)
                              taxAmount = formData.items.reduce((sum, it) => {
                                const itemTotal = (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 1);
                                const itemTaxRate = parseFloat(it.tax_rate) || 0;
                                return sum + (itemTotal * itemTaxRate / 100);
                              }, 0);
                            }
                            
                            const adjVal = parseFloat(formData.adjustment) || 0;
                            const adjustment = (formData.adjustment_sign === '-') ? -adjVal : adjVal;
                            
                            // Calculate final total based on discount type
                            let finalTotal;
                            if (formData.discount_type === 'before_tax') {
                              // Before Tax: (subtotal - discount) + tax + adjustment
                              finalTotal = (subtotal - discount) + taxAmount + adjustment;
                            } else {
                              // After Tax: subtotal + tax - discount + adjustment
                              finalTotal = subtotal + taxAmount - discount + adjustment;
                            }
                            
                            return formatCurrency(finalTotal);
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingQuotation(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save
                </button>
                <button
                  type="button"
                  onClick={handleSaveAndSend}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Save & Send
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default QuotationsManagement;
